import { defineNuxtPlugin } from '#app';
import type { Navigation } from '~~/server/types/navigation';
import { useBannersStore } from '~/store/banners.store';
import { useEventBadgesStore } from '~/store/eventBadges.store';
import { useLinksStore } from '~/store/links.store';
import { useNavigationStore } from '~/store/navigation.store';

export default defineNuxtPlugin(async (nuxtApp) => {
  if (nuxtApp._route.path && nuxtApp._route.path.startsWith('/healthcheck')) {
    return;
  }

  if (Object.hasOwn(nuxtApp, '$portal')) {
    return;
  }

  nuxtApp.hook('app:error', (err) => {
    console.error('app error', err);
  });

  nuxtApp.hook('vue:error', (err) => {
    console.error('vue error', err);
  });

  async function initializeData(language: string) {
    const data = await nuxtApp.runWithContext(() => usePortalInitialData(language));
    if (!data) return;

    nuxtApp.runWithContext(() => {
      useNavigationStore().setNavigations(data.navigations);
      useLinksStore().setLinks(data.links);
      useBannersStore().setBanners(language, data.banners);
      useEventBadgesStore().setBadges(language, data.eventBadges);
    });

    if (data.portalSettings) {
      nuxtApp.runWithContext(() => {
        useConfigState().value = data.portalSettings!;
      });
    }
  }

  const i18n = nuxtApp.$i18n as { locale: Ref<string>; defaultLocale: string } | undefined;
  const locale = i18n?.locale;
  const defaultLocale = i18n?.defaultLocale || 'et';
  await initializeData(locale?.value || defaultLocale);

  // Update route path on locale change
  nuxtApp.hook('portal:localeSwitch', async ({ oldLocale, newLocale }) => {
    if (oldLocale === newLocale) {
      return;
    }
    const portalPath = usePortalPath();
    let pathTo = portalPath.getLocalePath(newLocale);

    const { navigationPath } = usePaths();
    await initializeData(newLocale);
    const navigationStore = await nuxtApp.runWithContext(() => useNavigationStore());
    const routeName = String(nuxtApp._route.name ?? '');
    const isFrontpage = routeName.split('___')[0] === 'frontpage';
    if (!isFrontpage && navigationStore.currentNavigation?.id) {
      const currentId = navigationStore.currentNavigation.id;
      const newLocaleNavigation = navigationStore.navigations?.find((n: Navigation) => n.id === currentId);
      if (newLocaleNavigation) {
        navigationStore.setCurrentNavigation(newLocaleNavigation);
        pathTo = navigationPath(newLocaleNavigation, newLocale);
      }
    }

    const { path } = useRoute();
    const isHomepagePath = path === '/' || path.endsWith(`/${oldLocale}`);
    if (isHomepagePath) {
      pathTo = defaultLocale === newLocale ? '' : `/${newLocale}`;
    }

    return navigateTo(pathTo);
  });
});
